import {
  Users,
  Briefcase,
  Shield,
  Brain,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Upload,
  Search,
  FileText,
  BarChart3,
  Layers,
  Lock,
  Rocket,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ModuleFeature {
  text: string;
}

interface ModulePresentation {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  status: string;
  icon: LucideIcon;
  iconBg: string;
  features: ModuleFeature[];
}

const modules: ModulePresentation[] = [
  // {
  //   id: 'core',
  //   name: 'CORE',
  //   subtitle: 'Fondations & Administration',
  //   description:
  //     'Socle multi-tenant sécurisé avec gestion centralisée des utilisateurs, entreprises et abonnements.',
  //   icon: Shield,
  //   iconBg: 'bg-[var(--color-ink)]',
  //   features: [
  //     { text: 'Isolation multi-tenant complète des données par client' },
  //     { text: 'Authentification SSO centralisée (OIDC)' },
  //     { text: 'Gestion des entreprises : adhérents, prospects, clients' },
  //     { text: 'Système d\'abonnements avec crédits par module' },
  //     { text: 'Notifications unifiées (email, push) avec préférences' },
  //     { text: 'Audit complet et traçabilité de toutes les actions' },
  //   ],
  // },
  {
    id: 'itc',
    name: 'Information Technology Consulting',
    subtitle: 'Bras droit du commercial en ESN',
    description:
      'Du besoin client au positionnement du candidat : automatisez la collecte, le matching, la qualification et la réponse.',
    status: '',
    icon: Users,
    iconBg: 'bg-[var(--color-accent)]',
    features: [
      { text: 'SPoA  besoins / candidats' },
      { text: 'Matching besoins / profils' },
      { text: 'Qualification : détection d\'incohérences CV, Q&A techniques et fonctionnelles' },
      { text: 'Passage du CV au format client et rédaction du laïus de réponse' },
      { text: 'Planification et automatisation des relances' },
    ],
  },
  {
    id: 'tm',
    name: 'Tenders Management',
    subtitle: "Gestion des Appels d'Offres",
    description:
      'Suivi complet du cycle de vie des référencements, de la détection à la contractualisation.',
    status: 'En cours de développement',
    icon: Briefcase,
    iconBg: 'bg-[var(--color-success)]',
    features: [
      { text: 'SPoA  référencements' },
      { text: 'Pipeline commercial' },
      { text: 'Analyse des dossiers d’appel d’offre' },
      { text: 'Génération et personnalisation des réponses' },
      { text: 'Pilotage et automatisation du processus' },
    ],
  },
  {
    id: 'ra',
    name: 'Recruitment Accelerator',
    subtitle: 'ATS augmenté par l’IA',
    description:
      'Un ATS pour piloter vos recrutements, une IA pour accélérer votre sourcing.',
    status: 'Bientôt disponible',
    icon: Rocket,
    iconBg: 'bg-[var(--color-copper)]',
    features: [
      { text: 'Outil de sourcing intégré' },
      { text: 'Espace de travail collaboratif amélioré' },
      { text: 'Pipeline de recrutement partagé entre équipes' },
      { text: 'Suivi des entretiens et des retours collaborateurs' },
      { text: 'Tableaux de bord recruteurs et KPIs' },
      { text: 'Notifications et alertes en temps réel' },
    ],
  },
  {
    id: 'lg',
    name: 'Lead Generation',
    subtitle: 'Prospection Inbound & Outbound',
    description:
      'Générez et convertissez vos leads grâce à une stratégie inbound et une organisation outbound structurée.',
    status: 'Bientôt disponible',
    icon: Target,
    iconBg: 'bg-purple-600',
    features: [
      { text: 'Stratégie Inbound : contenus, landing pages, nurturing' },
      { text: 'CopyOps : génération et optimisation de contenus par IA' },
      { text: 'Organisation de l\'Outbound : séquences et ciblage' },
      { text: 'Pipeline de prospection avec suivi des interactions' },
      { text: 'Scoring et qualification automatique des leads' },
      { text: 'Reporting et analyse du taux de conversion' },
    ],
  },
];

interface TechHighlight {
  icon: LucideIcon;
  label: string;
  description: string;
}

const techHighlights: TechHighlight[] = [
  {
    icon: Brain,
    label: 'Intelligence Artificielle',
    description: "Extraction de CV et recherche sémantique alimentées par l'IA",
  },
  {
    icon: Search,
    label: 'Recherche Sémantique',
    description: 'Trouvez les bons profils en langage naturel, au-delà des mots-clés',
  },
  {
    icon: Shield,
    label: 'Multi-Tenant Sécurisé',
    description: 'Isolation complète des données entre chaque client',
  },
  {
    icon: Lock,
    label: 'Sécurité & Conformité',
    description: 'Audit complet, traçabilité et gestion fine des permissions',
  },
];

interface CapabilityItem {
  icon: LucideIcon;
  title: string;
  text: string;
}

const capabilities: CapabilityItem[] = [
  {
    icon: Upload,
    title: 'Import & Extraction',
    text: "Déposez un CV au format PDF ou Word et laissez l'IA extraire automatiquement toutes les informations structurées.",
  },
  {
    icon: Search,
    title: 'Recherche Sémantique',
    text: 'Trouvez les profils pertinents par description en langage naturel, au-delà de la simple recherche par mots-clés.',
  },
  {
    icon: FileText,
    title: 'Marchés Publics',
    text: "Travaillez sur les appels d’offres qui vous concernent en deux clics",
  },
  {
    icon: BarChart3,
    title: 'Tableaux de Bord',
    text: 'Suivez vos indicateurs clés en temps réel : candidats actifs, opportunités ouvertes, taux de conversion.',
  },
  {
    icon: Layers,
    title: 'Pipeline Commercial',
    text: 'Visualisez et pilotez vos opportunités dans un pipeline Kanban de la détection au contrat signé.',
  },
  {
    icon: Sparkles,
    title: 'Génération de CV',
    text: 'Produisez des CV professionnels formatés à partir de vos profils candidats, au template de votre choix.',
  },
];

export function HomePage() {
  const { isAuthenticated, login } = useAuth();

  // Rediriger vers le dashboard si l'utilisateur est déjà connecté
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-surface)]">
      {/* ═══ Hero Section ═══ */}
      <header className="relative overflow-hidden bg-[var(--color-ink)]">
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 py-24 md:py-32 text-center">
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-normal mb-8"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-text-inverse)' }}
          >
            BPA Ecosystem
          </h1>
          <p
            className="text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed"
            style={{ color: 'rgba(250, 250, 248, 0.7)' }}
          >
            La suite qui centralise vos applications métiers : référencements, pilotage commercial, gestion des talents, génération de lead
          </p>

          <button
            onClick={login}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-all hover:brightness-110"
            style={{
              backgroundColor: 'var(--color-copper)',
              color: 'var(--color-text-inverse)',
            }}
          >
            Se connecter
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* ═══ Applications Section ═══ */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="text-center mb-16">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
            style={{ color: 'var(--color-copper)' }}
          >
            Applications
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal mb-4"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Quatre applications, une plateforme
          </h2>
          <p
            className="text-base max-w-xl mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Chaque application couvre un aspect clé de votre activité.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <div
                key={module.id}
                className={`rounded-xl border p-6 md:p-8 transition-shadow hover:shadow-[var(--shadow-lg)] relative ${module.status ? 'opacity-75' : ''}`}
                style={{
                  backgroundColor: 'var(--color-surface-elevated)',
                  borderColor: module.status ? 'var(--color-copper)' : 'var(--color-border)',
                }}
              >
                {module.status && (
                  <div className="mb-4">
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
                      style={{
                        backgroundColor: 'var(--color-copper-muted)',
                        color: 'var(--color-copper)',
                        border: '1px solid var(--color-copper)',
                      }}
                    >
                      <span className="relative flex h-2 w-2">
                        <span
                          className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                          style={{ backgroundColor: 'var(--color-copper)' }}
                        />
                        <span
                          className="relative inline-flex rounded-full h-2 w-2"
                          style={{ backgroundColor: 'var(--color-copper)' }}
                        />
                      </span>
                      {module.status}
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-4 mb-5">
                  <div className={`p-3 rounded-lg shrink-0 ${module.iconBg}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--color-ink)' }}>
                      {module.name}
                    </h3>
                    <p className="text-sm font-medium" style={{ color: 'var(--color-copper)' }}>
                      {module.subtitle}
                    </p>
                  </div>
                </div>

                <p
                  className="text-sm leading-relaxed mb-5"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {module.description}
                </p>

                <ul className="space-y-2.5">
                  {module.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2
                        className="h-4 w-4 shrink-0 mt-0.5"
                        style={{ color: 'var(--color-success)' }}
                      />
                      <span style={{ color: 'var(--color-text-primary)' }}>{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ Capabilities Section ═══ */}
      <section
        className="border-t border-b"
        style={{
          backgroundColor: 'var(--color-surface-sunken)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="text-center mb-16">
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: 'var(--color-copper)' }}
            >
              Fonctionnalités clés
            </p>
            <h2
              className="text-3xl md:text-4xl font-normal mb-4"
              style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
            >
              Des outils qui font la différence
            </h2>
            <p
              className="text-base max-w-xl mx-auto"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Des fonctionnalités pensées pour accélérer chaque étape de votre activité.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="flex gap-4">
                  <div
                    className="h-10 w-10 rounded-lg shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-accent-light)' }}
                  >
                    <Icon className="h-5 w-5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <h3
                      className="text-base font-semibold mb-1"
                      style={{ color: 'var(--color-ink)' }}
                    >
                      {cap.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {cap.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Tech Highlights ═══ */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="text-center mb-16">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
            style={{ color: 'var(--color-copper)' }}
          >
            Technologies
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal mb-4"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Propulsé par l'IA et le cloud
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {techHighlights.map((tech) => {
            const Icon = tech.icon;
            return (
              <div
                key={tech.label}
                className="text-center rounded-xl border p-6 transition-shadow hover:shadow-[var(--shadow-md)]"
                style={{
                  backgroundColor: 'var(--color-surface-elevated)',
                  borderColor: 'var(--color-border)',
                }}
              >
                <div
                  className="h-12 w-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-copper-muted)' }}
                >
                  <Icon className="h-6 w-6" style={{ color: 'var(--color-copper)' }} />
                </div>
                <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-ink)' }}>
                  {tech.label}
                </h3>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {tech.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ Footer CTA ═══ */}
      <footer className="border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2
            className="text-2xl md:text-3xl font-normal mb-4"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Prêt à transformer votre activité ?
          </h2>
          <p
            className="text-base mb-8 max-w-lg mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Connectez-vous pour accéder à l'ensemble des modules et commencer à optimiser vos
            processus.
          </p>
          <button
            onClick={login}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-all hover:brightness-110"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-text-inverse)',
            }}
          >
            Commencer maintenant
            <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-8 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            BPA Ecosystem
            <span className="mx-2">·</span>
            <a
              href="/contact"
              className="underline hover:opacity-80 transition-opacity"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              Contact
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
